import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentArrowDownIcon,
  PrinterIcon, // Add PrinterIcon
  UsersIcon,
  CurrencyRupeeIcon,
  CalendarDaysIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface ClientStats {
  id: number;
  name: string;
  director: string;
  orderCount: number;
  totalSpend: number;
  lastOrderDate: string;
  averageDiscount: number;
  referralSource: string;
}

const ClientReport: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clientStats, setClientStats] = useState<ClientStats[]>([]);
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('totalSpend');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Effect to fetch data when period, dates, or sorting changes
  useEffect(() => {
    const fetchData = async () => {
      console.log("Fetching client report data with period:", period, "from", startDate, "to", endDate);
      setLoading(true);
      let startToFetch = startDate;
      let endToFetch = endDate;
      let updateStateDates = false;

      if (period !== 'custom') {
        const today = new Date();
        const calculatedStart = new Date();
        if (period === 'month') {
          calculatedStart.setMonth(today.getMonth() - 1);
        } else if (period === 'quarter') {
          calculatedStart.setMonth(today.getMonth() - 3);
        } else if (period === 'year') {
          calculatedStart.setFullYear(today.getFullYear() - 1);
        }
        const calculatedStartDateStr = calculatedStart.toISOString().split('T')[0];
        const calculatedEndDateStr = today.toISOString().split('T')[0];

        startToFetch = calculatedStartDateStr;
        endToFetch = calculatedEndDateStr;
        if (startDate !== calculatedStartDateStr || endDate !== calculatedEndDateStr) {
          updateStateDates = true;
        }
      }

      if (!startToFetch || !endToFetch) {
        if (period === 'custom') {
          console.log("Custom period selected but dates not set, defaulting to 'month'");
          setPeriod('month'); // Default to month if custom dates are invalid
          setLoading(false);
          return;
        }
        console.error("Invalid date range for fetch:", startToFetch, endToFetch);
        setLoading(false);
        setClientStats([]);
        return;
      }

      try {
        const response = await axios.get('/reports/clients', {
          params: {
            startDate: startToFetch,
            endDate: endToFetch,
            sortBy,
            sortOrder
          }
        });
        // Extract the 'clients' array from the response
        const clientsData = response.data?.clients || []; 
        setClientStats(clientsData);
        
        // TODO: Set summary, top clients etc. into separate state if needed
        // setReportSummary(response.data?.summary || null);
        // setTopSpendingClients(response.data?.topClientsBySpend || []);
        // setTopOrderingClients(response.data?.topClientsByOrderCount || []);

        if (updateStateDates) {
          if (!startDate) setStartDate(startToFetch);
          if (!endDate) setEndDate(endToFetch);
        }
      } catch (error: any) { // Added type annotation for error
        console.error('Error fetching client report data:', error);
        setClientStats([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [period, sortBy, sortOrder]); // Added startDate and endDate dependencies
  
  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    setStartDate('');
    setEndDate('');
  };
  
  const handleDateRangeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Update state, useEffect will trigger fetch
    if (startDate && endDate) {
      setPeriod('custom' + startDate + endDate);
    }
  };
  
  // Removed separate fetchClientStats function
  
  const handleSortChange = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column and default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Open printable view in a new tab
  const handleOpenPrintView = () => {
    if (!startDate || !endDate) {
      alert('Пожалуйста, выберите или примените период для печати отчета.');
      return;
    }
    // Construct the URL for the print page with query parameters
    const printUrl = `/reports/clients/print?startDate=${startDate}&endDate=${endDate}`;
    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }
  };

  // Filter clients by search term
  const filteredClients = clientStats.filter(client => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      client.name.toLowerCase().includes(searchLower) ||
      (client.director && client.director.toLowerCase().includes(searchLower))
    );
  });
  
  // Format currency
  const formatCurrency = (amount: number | null | undefined) => { // Allow null/undefined
    // Return '0 ₸' if amount is not a valid number
    if (typeof amount !== 'number' || isNaN(amount)) {
      return '0 ₸'; 
    }
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  // Calculate totals
  const totalRevenue = filteredClients.reduce((sum: number, client: ClientStats) => {
    const spend = client.totalSpend;
    // Add spent only if it's a valid number, otherwise add 0
    return sum + (typeof spend === 'number' && !isNaN(spend) ? spend : 0);
  }, 0);
  const totalOrders = filteredClients.reduce((sum: number, client: ClientStats) => sum + client.orderCount, 0);
  // Removed unused avgDiscount calculation
  
  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Нет данных';
    return new Date(dateString).toLocaleDateString('ru-KZ');
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title="Отчет по клиентам"
        subtitle={`Статистика по клиентам за период: ${startDate ? new Date(startDate).toLocaleDateString('ru-KZ') : '?'} - ${endDate ? new Date(endDate).toLocaleDateString('ru-KZ') : '?'}`}
        actions={
          <button
            onClick={handleOpenPrintView} // Use new handler
            className="btn-secondary flex items-center"
            disabled={!startDate || !endDate} // Disable if dates aren't set
          >
            <PrinterIcon className="h-5 w-5 mr-1" /> {/* Use PrinterIcon */}
            Печать
          </button>
        }
      />
      
      {/* Period selection */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 justify-between mb-4">
          <div className="flex space-x-2">
            <button
              className={`px-3 py-1 rounded ${period === 'month' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('month')}
            >
              Месяц
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'quarter' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('quarter')}
            >
              Квартал
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'year' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('year')}
            >
              Год
            </button>
          </div>
          
          <div className="relative">
            <input
              type="text"
              placeholder="Поиск клиента..."
              className="form-input pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <UsersIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
        </div>
        
        <form onSubmit={handleDateRangeSubmit} className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Начальная дата</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Конечная дата</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <button type="submit" className="btn-primary flex items-center">
            <CalendarDaysIcon className="h-5 w-5 mr-1" />
            Применить
          </button>
        </form>
      </div>
      
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Общая выручка</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalRevenue)}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CurrencyRupeeIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Всего клиентов</p>
              <p className="text-2xl font-bold text-gray-900">{filteredClients.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <UsersIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Всего заказов</p>
              <p className="text-2xl font-bold text-gray-900">{totalOrders}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <ClipboardDocumentCheckIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Client table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('name')}
                >
                  Клиент
                  {sortBy === 'name' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('orderCount')}
                >
                  Заказы
                  {sortBy === 'orderCount' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('totalSpend')}
                >
                  Сумма
                  {sortBy === 'totalSpend' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('lastOrderDate')}
                >
                  Последний заказ
                  {sortBy === 'lastOrderDate' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('averageDiscount')}
                >
                  Ср. скидка
                  {sortBy === 'averageDiscount' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('referralSource')}
                >
                  Источник
                  {sortBy === 'referralSource' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredClients.length > 0 ? (
                filteredClients.map((client) => (
                  <tr 
                    key={client.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/clients/${client.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {client.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {client.director}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {client.orderCount}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {formatCurrency(client.totalSpend)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(client.lastOrderDate)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {client.averageDiscount}%
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {client.referralSource || 'Не указан'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                    Нет данных для отображения
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}; // Ensure closing brace is present

export default ClientReport;
